import { Box, Group, ScrollArea, Select, Text } from '@mantine/core';

import { IconChevronDown } from '@tabler/icons-react';

import { SidepanelHeader } from '../../../shared/components/UI/Sidepanel/Sidepanel';
import { useSidepanelContext } from '../../../shared/components/UI/Sidepanel/SidepanelInternalData.provider';

import type { DocumentChunk } from '../../../shared/entities/DocumentChunk';
import type { TengoDocument } from '../../../shared/entities/TengoDocument';
import { encodeFilePathWithEncodedParenthesis } from '../../../shared/utils/strings';
import { DownloadFileButton } from './DownloadFileButton';
import { SourceExtractPreview } from './SourceExtractPreview';
import { formatSourceLabel } from './SourcesSection';
import {
  DocumentPreview,
  SUPPORTED_OFFICE_EXTENSIONS,
} from './preview/DocumentPreview';

export const SOURCE_SIDE_PANEL_WIDTH = 800;

export type SourceSidepanelContext = {
  selectedSource: DocumentChunk;
};

export function SourceSidePanel({
  documents,
  sources = [],
  closePanel,
}: {
  documents: Pick<
    TengoDocument,
    'prettyName' | 'S3FileURL' | 'S3PreviewFileURL'
  >[];
  sources?: DocumentChunk[];
  closePanel: () => void;
}) {
  const {
    data: { selectedSource },
    updateData,
  } = useSidepanelContext<SourceSidepanelContext>();

  const handleSourceChange = (source: DocumentChunk) => {
    const newSelectedSource = sources.find(
      src => src.pageContent === source.pageContent,
    );

    if (!newSelectedSource) {
      throw new Error('Source not found');
    }

    updateData({ selectedSource: newSelectedSource });
  };

  const document = getSelectedDocument(documents, selectedSource);

  return (
    <>
      <SidepanelHeader
        closePanel={closePanel}
        header={
          <Header
            sources={sources}
            onSourceChange={handleSourceChange}
            document={document}
          />
        }
      />
      <Content selectedSource={selectedSource} selectedDocument={document} />
    </>
  );
}

function Header({
  sources,
  onSourceChange,
  document,
}: {
  sources: DocumentChunk[];
  onSourceChange: (source: DocumentChunk) => void;
  document: Pick<
    TengoDocument,
    'prettyName' | 'S3FileURL' | 'S3PreviewFileURL'
  >;
}) {
  const {
    data: { selectedSource },
    updateData,
  } = useSidepanelContext<SourceSidepanelContext>();

  const initialHighlightedSourceIndex = sources.findIndex(
    ({ metadata }) =>
      metadata.filePath === selectedSource.metadata.filePath &&
      metadata.pageNumber === selectedSource.metadata.pageNumber,
  );

  const selectData = sources.map((source, index) => ({
    value: index.toString(),
    label: formatSourceLabel(source),
  }));

  function handleSourceChange(value: string) {
    const source = sources[parseInt(value)];
    onSourceChange(source);
    updateData({ selectedSource: source });
  }

  return sources.length > 1 ? (
    <Group noWrap position="apart" w="100%">
      <Select
        styles={theme => ({
          root: { minWidth: '450px' },
          input: {
            border: 'none',
            textAlign: 'center',
            fontWeight: 500,
            color: theme.colors.gray[9],
            fontSize: theme.fontSizes.sm,
          },
          dropdown: { minWidth: 'fit-content' },
          item: { wordSpacing: 'nowrap' },
          rightSection: { pointerEvents: 'none' },
        })}
        rightSection={<IconChevronDown size={16} />}
        value={initialHighlightedSourceIndex.toString()}
        data={selectData}
        onChange={handleSourceChange}
      />

      <DownloadFileButton
        S3FileURL={document.S3FileURL}
        prettyName={document.prettyName}
      />
    </Group>
  ) : (
    <Text variant="sm" fw={400} c="gray.9" w="100%" align="center" truncate>
      {document.prettyName}
    </Text>
  );
}

function Content({
  selectedSource,
  selectedDocument,
}: {
  selectedSource: DocumentChunk;
  selectedDocument: Pick<TengoDocument, 'prettyName' | 'S3PreviewFileURL'>;
}) {
  const extension =
    selectedDocument.S3PreviewFileURL.split('.').pop()?.toLowerCase() || '';
  const isDocX = SUPPORTED_OFFICE_EXTENSIONS.includes(extension);

  return (
    <Box px="04" w={SOURCE_SIDE_PANEL_WIDTH} h="100%">
      {isDocX ? (
        <ScrollArea h="95%" sx={{ flexGrow: 1 }}>
          <SourceExtractPreview extract={selectedSource.pageContent} />
        </ScrollArea>
      ) : (
        <DocumentPreview
          search={selectedSource.pageContent}
          initialPage={selectedSource.metadata.pageNumber ?? 1}
          S3PreviewFileURL={selectedDocument.S3PreviewFileURL}
          prettyName={selectedDocument.prettyName}
        />
      )}
    </Box>
  );
}

function getSelectedDocument(
  documents: Pick<
    TengoDocument,
    'prettyName' | 'S3FileURL' | 'S3PreviewFileURL'
  >[],
  selectedSource: DocumentChunk,
) {
  const encodedPath = encodeFilePathWithEncodedParenthesis(
    selectedSource.metadata.filePath,
  );

  const selectedDocument = documents.find(
    doc =>
      doc.S3PreviewFileURL.includes(encodedPath) ||
      doc.S3PreviewFileURL.includes(selectedSource.metadata.filePath) ||
      doc.S3FileURL.includes(selectedSource.metadata.filePath) ||
      doc.S3FileURL.includes(encodedPath),
  );

  if (!selectedDocument) {
    throw new Error('Selected document not found');
  }

  return selectedDocument;
}
